import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Edit, Loader2, Plus, Save, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormLabel } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Textarea } from '../../../components/ui/textarea';
import { historyTemplatesApi, templateVariablesApi, type HistoryTemplateDto, type TemplateVariableMeta } from '../../../lib/api';
import { formatDateTime } from '../../../lib/dateFormat';
import {
  GeltungAuswahl,
  GeltungHinweis,
  useStandortOptionen,
  useStandortVorlagen,
  type VorlagenEbene,
} from './standortGeltung';

const extractPlaceholders = (s: string): string[] => {
  const out: string[] = [];
  const re = /\{\{([^}]+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s || ''))) {
    const v = (m[1] || '').trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out.sort();
};

const HistoryTemplates = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [templates, setTemplates] = useState<HistoryTemplateDto[]>([]);
  const [zuLoeschen, setZuLoeschen] = useState<HistoryTemplateDto | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<HistoryTemplateDto | null>(null);
  const [allowedVarsMeta, setAllowedVarsMeta] = useState<TemplateVariableMeta[]>([]);
  const historyEditorRef = useRef<HTMLTextAreaElement | null>(null);
  const taskEditorRef = useRef<HTMLTextAreaElement | null>(null);

  // Wofür gepflegt wird: global (null) oder ein Standort — wie bei den E-Mail-Vorlagen.
  const standorte = useStandortOptionen();
  const [geltung, setGeltung] = useState<number | null>(null);
  const [ebene, setEbene] = useState<VorlagenEbene>('global');
  const geltungName = standorte.find((s) => s.id === geltung)?.name ?? '';
  const { vorlagen: standortVorlagen, merken, entfernen } = useStandortVorlagen(geltung, (id) =>
    historyTemplatesApi.list(undefined, id),
  );

  const [templateKey, setTemplateKey] = useState('');
  const [templateCategory, setTemplateCategory] = useState('history');
  const [templateHistoryBody, setTemplateHistoryBody] = useState('');
  const [templateTaskEnabled, setTemplateTaskEnabled] = useState(false);
  const [templateTaskBody, setTemplateTaskBody] = useState('');
  const [templateTaskDueOffsetValue, setTemplateTaskDueOffsetValue] = useState<number>(1);
  const [templateTaskDueOffsetUnit, setTemplateTaskDueOffsetUnit] = useState('days');
  const [templateDescription, setTemplateDescription] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const list = await historyTemplatesApi.list();
        setTemplates(list);
      } catch (e: any) {
        toast.error(e?.message || 'Fehler beim Laden der Historien-Vorlagen');
      }
    };
    void load();
  }, []);

  const filteredTemplates = templates.filter((t) =>
    t.template_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.category || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allowedVars = useMemo(() => allowedVarsMeta.map((v) => v.key), [allowedVarsMeta]);
  const usedVars = useMemo(() => {
    const all = [...extractPlaceholders(templateHistoryBody), ...extractPlaceholders(templateTaskBody)];
    return Array.from(new Set(all)).sort();
  }, [templateHistoryBody, templateTaskBody]);
  const unknownVars = useMemo(() => usedVars.filter((v) => !allowedVars.includes(v)), [usedVars, allowedVars]);

  const loadTemplateContext = async (templateKey: string) => {
    try {
      const vars = await templateVariablesApi.getMeta(templateKey);
      setAllowedVarsMeta(vars || []);
    } catch {
      setAllowedVarsMeta([]);
    }
  };

  const handleEditTemplate = async (template: HistoryTemplateDto) => {
    // Gibt es für den gewählten Standort eine eigene Vorlage, wird die bearbeitet.
    const eigene = geltung !== null ? standortVorlagen[template.template_key] : undefined;
    const quelle = eigene ?? template;
    setEbene(eigene ? 'standort' : 'global');
    setSelectedTemplate(template);
    setTemplateKey(template.template_key || '');
    setTemplateCategory(quelle.category || 'history');
    setTemplateHistoryBody(quelle.history_body || '');
    setTemplateTaskEnabled(Boolean(Number(quelle.auto_create_task)));
    setTemplateTaskBody(quelle.task_body || '');
    setTemplateTaskDueOffsetValue(Number(quelle.task_due_offset_value || 1));
    setTemplateTaskDueOffsetUnit(quelle.task_due_offset_unit || 'days');
    setTemplateDescription(quelle.description || '');
    setIsDialogOpen(true);
    await loadTemplateContext(template.template_key);
  };

  const handleCreateTemplate = () => {
    setEbene('global');
    setSelectedTemplate(null);
    setTemplateKey('');
    setTemplateCategory('history');
    setTemplateHistoryBody('');
    setTemplateTaskEnabled(false);
    setTemplateTaskBody('');
    setTemplateTaskDueOffsetValue(1);
    setTemplateTaskDueOffsetUnit('days');
    setTemplateDescription('');
    setAllowedVarsMeta([]);
    setIsDialogOpen(true);
  };

  const insertTokenAtCursor = (
    token: string,
    ref: React.RefObject<HTMLTextAreaElement | null>,
    value: string,
    setValue: (next: string) => void
  ) => {
    const el = ref.current;
    if (!el) {
      setValue(value + token);
      return;
    }
    const start = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
    const next = value.slice(0, start) + token + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch {
        // noop
      }
    });
  };

  const handleSaveTemplate = async () => {
    setIsLoading(true);
    try {
      const key = templateKey.trim();
      if (!key) throw new Error('Template-Key ist erforderlich');
      if (!templateHistoryBody.trim()) throw new Error('Historientext ist erforderlich');
      if (templateTaskEnabled && !templateTaskBody.trim()) throw new Error('Aufgabentext ist erforderlich, wenn Auto-Aufgabe aktiv ist');
      if (templateTaskEnabled && !templateTaskDueOffsetValue) throw new Error('Due-Offset ist erforderlich, wenn Auto-Aufgabe aktiv ist');

      const inhalt = {
        category: templateCategory,
        history_body: templateHistoryBody,
        description: templateDescription || null,
        auto_create_task: templateTaskEnabled,
        task_body: templateTaskEnabled ? templateTaskBody : null,
        task_assignee_strategy: 'executing_user',
        task_due_offset_value: templateTaskEnabled ? templateTaskDueOffsetValue : null,
        task_due_offset_unit: templateTaskEnabled ? templateTaskDueOffsetUnit : null,
      };
      if (selectedTemplate?.template_key && ebene !== 'global' && geltung !== null) {
        const gespeichert = ebene === 'standort'
          ? await historyTemplatesApi.update(key, inhalt, geltung)
          : await historyTemplatesApi.create({ template_key: key, ...inhalt, standort_id: geltung });
        merken(gespeichert);
        toast.success(`Vorlage für ${geltungName} gespeichert`);
      } else if (selectedTemplate?.template_key) {
        const updated = await historyTemplatesApi.update(selectedTemplate.template_key, inhalt);
        setTemplates((prev) => prev.map((t) => (t.template_key === updated.template_key ? updated : t)));
        toast.success(geltung === null ? 'Historien-Vorlage wurde gespeichert' : 'Globale Vorlage gespeichert');
      } else {
        const created = await historyTemplatesApi.create({ template_key: key, ...inhalt });
        setTemplates((prev) => [created, ...prev]);
        toast.success('Historien-Vorlage wurde angelegt');
      }
      setIsDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.message || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const key = templateKey.trim();
    if (!key) {
      setAllowedVarsMeta([]);
      return;
    }
    void loadTemplateContext(key);
  }, [templateKey]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Historien-Vorlagen</h1>
          <p className="text-muted-foreground">Vorlagen für Historieneinträge mit optionaler Auto-Aufgabe</p>
        </div>
        <Button onClick={handleCreateTemplate}><Plus className="mr-2 h-4 w-4" />Neue Vorlage</Button>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <GeltungAuswahl standorte={standorte} geltung={geltung} onChange={setGeltung} />
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input uiSize="md" placeholder="Vorlagen durchsuchen..." className="pl-8" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Historien-Vorlagen</CardTitle>
          <CardDescription>Liste aller verfügbaren Historien-Templates</CardDescription>
        </CardHeader>
        <CardContent>
          <Table density="compact">
            <TableHeader>
              <TableRow>
                <TableHead density="compact">Key</TableHead>
                <TableHead density="compact">Kategorie</TableHead>
                <TableHead density="compact">Auto-Aufgabe</TableHead>
                <TableHead density="compact">Status</TableHead>
                {geltung !== null ? <TableHead density="compact">Gilt in {geltungName}</TableHead> : null}
                <TableHead density="compact">Zuletzt bearbeitet</TableHead>
                <TableHead density="compact" className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.template_key}>
                  <TableCell density="compact" className="font-medium">{template.template_key}</TableCell>
                  <TableCell density="compact">{template.category}</TableCell>
                  <TableCell density="compact">
                    <Badge size="sm" variant={Number(template.auto_create_task) ? 'default' : 'secondary'}>
                      {Number(template.auto_create_task) ? 'aktiv' : 'aus'}
                    </Badge>
                  </TableCell>
                  <TableCell density="compact">
                    <Badge size="sm" variant={Number(template.is_active) ? 'success' : 'secondary'}>
                      {Number(template.is_active) ? 'aktiv' : 'inaktiv'}
                    </Badge>
                  </TableCell>
                  {geltung !== null ? (
                    <TableCell density="compact">
                      {standortVorlagen[template.template_key] ? (
                        <Badge size="sm" variant="default">eigene Vorlage</Badge>
                      ) : (
                        <Badge size="sm" variant="secondary">globale Vorlage</Badge>
                      )}
                    </TableCell>
                  ) : null}
                  <TableCell density="compact">{formatDateTime(template.updated_at)}</TableCell>
                  <TableCell density="compact" className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => void handleEditTemplate(template)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setZuLoeschen(template)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell density="compact" colSpan={geltung !== null ? 7 : 6} className="h-24 text-center">
                    Keine Vorlagen gefunden.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedTemplate ? 'Historien-Vorlage bearbeiten' : 'Neue Historien-Vorlage erstellen'}</DialogTitle>
            <DialogDescription>Historientext und optionale Auto-Aufgabe konfigurieren.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {selectedTemplate ? (
              <GeltungHinweis
                geltung={geltung}
                geltungName={geltungName}
                ebene={ebene}
                onEigeneAnlegen={() => setEbene('standort_neu')}
              />
            ) : null}
            <div className="grid gap-2">
              <FormLabel>Template-Key</FormLabel>
              <Input
                uiSize="md"
                value={templateKey}
                onChange={(e) => setTemplateKey(e.target.value)}
                placeholder="z.B. trial.cancelled.history"
                disabled={!!selectedTemplate}
              />
            </div>

            <div className="grid gap-2">
              <FormLabel>Kategorie</FormLabel>
              <Select value={templateCategory} onValueChange={setTemplateCategory}>
                <SelectTrigger size="md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="history">history</SelectItem>
                  <SelectItem value="trial">trial</SelectItem>
                  <SelectItem value="trial_request">trial_request</SelectItem>
                  <SelectItem value="course">course</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <FormLabel>Beschreibung</FormLabel>
              <Input uiSize="md" value={templateDescription} onChange={(e) => setTemplateDescription(e.target.value)} />
            </div>

            <div className="grid gap-2">
              <FormLabel>Historientext</FormLabel>
              <Textarea ref={historyEditorRef} value={templateHistoryBody} onChange={(e) => setTemplateHistoryBody(e.target.value)} rows={4} />
              <div className="flex flex-wrap gap-2">
                {allowedVarsMeta.map((v) => (
                  <button
                    key={`history-${v.key}`}
                    type="button"
                    onClick={() => insertTokenAtCursor(`{{${v.key}}}`, historyEditorRef, templateHistoryBody, setTemplateHistoryBody)}
                    className="cursor-pointer"
                  >
                    <Badge size="sm" variant="secondary">{v.label}</Badge>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-md border p-3">
              <div className="mb-2 font-medium">Optionale Auto-Aufgabe</div>
              <div className="mb-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={templateTaskEnabled} onChange={(e) => setTemplateTaskEnabled(e.target.checked)} />
                  Aufgabe automatisch anlegen
                </label>
              </div>
              {templateTaskEnabled ? (
                <div className="grid gap-3">
                  <div className="grid gap-2">
                    <FormLabel>Aufgabentext</FormLabel>
                    <Textarea ref={taskEditorRef} value={templateTaskBody} onChange={(e) => setTemplateTaskBody(e.target.value)} rows={3} />
                    <div className="flex flex-wrap gap-2">
                      {allowedVarsMeta.map((v) => (
                        <button
                          key={`task-${v.key}`}
                          type="button"
                          onClick={() => insertTokenAtCursor(`{{${v.key}}}`, taskEditorRef, templateTaskBody, setTemplateTaskBody)}
                          className="cursor-pointer"
                        >
                          <Badge size="sm" variant="secondary">{v.label}</Badge>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <FormLabel>Due-Offset Wert</FormLabel>
                      <Input
                        type="number"
                        value={templateTaskDueOffsetValue}
                        onChange={(e) => setTemplateTaskDueOffsetValue(Number(e.target.value))}
                      />
                    </div>
                    <div className="grid gap-2">
                      <FormLabel>Due-Offset Einheit</FormLabel>
                      <Select value={templateTaskDueOffsetUnit} onValueChange={setTemplateTaskDueOffsetUnit}>
                        <SelectTrigger size="md"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minutes">minutes</SelectItem>
                          <SelectItem value="hours">hours</SelectItem>
                          <SelectItem value="days">days</SelectItem>
                          <SelectItem value="weeks">weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Assignee ist aktuell immer der ausführende Benutzer (`executing_user`).</p>
                </div>
              ) : null}
            </div>

            <div className="rounded-md border p-3 text-sm">
              <div className="mb-2 font-medium">Variablen</div>
              <div className="flex flex-wrap gap-2">
                {allowedVarsMeta.length === 0 ? (
                  <span className="text-xs text-muted-foreground">Keine Variablen-Meta verfügbar (bei bestehendem Key automatisch geladen)</span>
                ) : (
                  allowedVarsMeta.map((v) => <Badge size="sm" key={v.key} variant="secondary">{v.label}</Badge>)
                )}
              </div>
              {unknownVars.length > 0 ? (
                <div className="mt-3 text-xs text-destructive">Unbekannte Variablen: {unknownVars.map((v) => `{{${v}}}`).join(', ')}</div>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveTemplate} disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={
          geltung !== null && zuLoeschen && standortVorlagen[zuLoeschen.template_key]
            ? `Eigene Vorlage von ${geltungName} entfernen?`
            : `Vorlage „${zuLoeschen?.template_key ?? ''}“ deaktivieren?`
        }
        beschreibung={
          geltung !== null && zuLoeschen && standortVorlagen[zuLoeschen.template_key]
            ? 'Danach gilt an diesem Standort wieder die globale Vorlage. Der Grund wird im Protokoll festgehalten.'
            : 'Die Vorlage wird deaktiviert und steht danach nicht mehr zur Auswahl. Der Grund wird im Protokoll festgehalten.'
        }
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (!zuLoeschen) return;
          const key = zuLoeschen.template_key;
          if (geltung !== null && standortVorlagen[key]) {
            await historyTemplatesApi.delete(key, grund, geltung);
            entfernen(key);
            setZuLoeschen(null);
            toast.success(`Eigene Vorlage von ${geltungName} entfernt`);
            return;
          }
          await historyTemplatesApi.delete(key, grund);
          setTemplates((prev) => prev.map((t) => (t.template_key === key ? { ...t, is_active: 0 } : t)));
          setZuLoeschen(null);
          toast.success('Vorlage wurde deaktiviert');
        }}
      />
    </div>
  );
};

export default HistoryTemplates;
